<template>
  <v-card flat>
    <v-card-text>
      <div class="d-flex justify-end mb-4">
        <v-btn
          icon
          color="grey"
          class="mr-2"
          @click="fetchSubroles"
          :loading="loading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn color="primary" @click="$emit('create')">
          <v-icon left>mdi-plus</v-icon>
          Crear perfil de acceso
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="roles"
        :loading="loading"
        loading-text="Cargando subroles..."
        class="elevation-1"
        hide-default-footer
        :items-per-page="-1"
      >
        <template v-slot:item.name="{ item }">
          <div class="font-weight-bold py-2">
            {{ item.name }}
            <v-chip v-if="item.isSystem" x-small color="grey" dark class="ml-2">
              Rol base
            </v-chip>
          </div>
          <div class="text-caption grey--text">
            {{ item.description || "Sin descripción" }}
          </div>
        </template>

        <template v-slot:item.usersCount="{ item }">
          <v-chip small outlined color="primary">
            {{ item.usersCount }}
            {{
              item.isSystem
                ? " usuario(s) sin perfil de acceso"
                : " usuario(s) con este perfil de acceso"
            }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              v-if="!item.isSystem"
              icon
              color="primary"
              small
              class="mr-2"
              @click="$emit('edit', item)"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-tooltip
              v-if="!item.isSystem"
              bottom
              :disabled="item.usersCount === 0"
            >
              <template v-slot:activator="{ on }">
                <span v-on="on" class="d-inline-flex">
                  <v-btn
                    icon
                    color="error"
                    small
                    :disabled="
                      item.usersCount > 0 || deleteLoadingId === item.id
                    "
                    :loading="deleteLoadingId === item.id"
                    @click="confirmDelete(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>
                No se puede eliminar: hay usuarios con este subrol asignado.
              </span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import { GetSubroles, DeleteSubrole } from "@/api/subrole-api";

export default {
  name: "RolePermissionsList",
  data() {
    return {
      loading: false,
      deleteLoadingId: null,
      headers: [
        {
          text: "Perfil de acceso",
          value: "name",
          align: "start",
          sortable: true
        },
        {
          text: "Usuarios",
          value: "usersCount",
          align: "center",
          sortable: true
        },
        { text: "Acciones", value: "actions", align: "end", sortable: false }
      ],
      defaultRoles: [
        {
          id: "admin",
          name: "Admin",
          description:
            "Usuarios con rol Admin que no tienen un subrol personalizado asignado.",
          isSystem: true
        },
        {
          id: "broker",
          name: "Broker",
          description:
            "Usuarios con rol Broker que no tienen un subrol personalizado asignado.",
          isSystem: true
        }
      ],
      customSubroles: []
    };
  },
  computed: {
    ...mapState("contacts", ["contactList"]),

    userCounts() {
      const counts = {
        admin: 0,
        broker: 0
      };

      if (!this.contactList) return counts;

      const filteredContacts = this.contactList
        .filter(item => {
          return item.status !== 0 || item.performance.totalLeads >= 1;
        });

      // Contact list: solo subrole_id para subroles custom (no viene subrole poblado)
      filteredContacts.forEach(user => {
        if (user.subrole_id) {
          const key = String(user.subrole_id);
          counts[key] = (counts[key] || 0) + 1;
        } else if (user.role) {
          const roleKey = user.role.toLowerCase();
          counts[roleKey] = (counts[roleKey] || 0) + 1;
        }
      });

      return counts;
    },

    roles() {
      return [...this.defaultRoles, ...this.customSubroles].map(role => ({
        ...role,
        usersCount: this.userCounts[role.id] || 0
      }));
    }
  },
  created() {
    this.fetchSubroles();
  },
  methods: {
    async fetchSubroles() {
      this.loading = true;
      try {
        const res = await GetSubroles();
        this.customSubroles = res.subroles.map(r => ({
          ...r,
          id: r._id,
          isSystem: false
        }));
      } catch (err) {
        this.$snotify.error("Error al cargar la lista de subroles", "Error");
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(role) {
      if (role.usersCount > 0) return;
      if (
        !confirm(
          `¿Eliminar el subrol "${role.name}"? Esta acción no se puede deshacer.`
        )
      ) {
        return;
      }
      this.deleteSubrole(role);
    },

    async deleteSubrole(role) {
      const subroleId = role._id;
      if (!subroleId) {
        this.$snotify.error("No se pudo identificar el subrol.", "Error");
        return;
      }
      this.deleteLoadingId = subroleId;
      try {
        await DeleteSubrole(subroleId);
        this.$snotify.success("Subrol eliminado correctamente", "Completado");
        await this.fetchSubroles();
      } catch (err) {
        this.$snotify.error(
          "No se pudo eliminar el subrol: " + (err.message || err),
          "Error"
        );
      } finally {
        this.deleteLoadingId = null;
      }
    }
  }
};
</script>
